import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/hooks/useApi";
import { Field, StatusBadge } from "@/components/common/bits";
import { cn } from "@/lib/utils";

export default function VendorQuestionnaire() {
  const { token } = useParams();
  const { data, error } = useApi(`/vendor-questionnaire/${token}`);
  const [answers, setAnswers] = useState({});
  const [meta, setMeta] = useState({ respondent: "", comments: "" });
  const [result, setResult] = useState(null);
  const submit = async () => { try { const r = await api.post(`/vendor-questionnaire/${token}`, { answers, ...meta }); setResult(r.data); } catch (e) { toast.error(errMsg(e)); } };

  if (error) return <div className="min-h-screen grid place-items-center text-muted-foreground" data-testid="vq-not-found">This questionnaire link is invalid.</div>;
  if (!data) return null;
  const done = data.submitted || result;
  return (
    <div className="min-h-screen bg-[hsl(var(--sidebar))]">
      <main className="max-w-2xl mx-auto px-6 py-12">
        <div className="flex items-center gap-2 mb-8"><div className="h-8 w-8 rounded-md bg-primary text-primary-foreground grid place-items-center font-display font-extrabold">C</div><span className="font-display font-bold">ComplyKosh</span><span className="eyebrow ml-3">Vendor security questionnaire</span></div>
        <div className="surface p-8">
          <div className="eyebrow">Requested by {data.org}</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold mt-1" data-testid="vq-vendor">{data.vendor}</h1>
          {done ? (
            <div className="text-center py-10" data-testid="vq-done"><CheckCircle2 size={44} className="mx-auto text-emerald-500" /><div className="font-display font-semibold text-lg mt-3">Thank you — responses submitted</div>{result && <div className="text-sm text-muted-foreground mt-1">Preliminary risk rating: <StatusBadge status={result.risk_level}>{result.risk_score} · {result.risk_level}</StatusBadge></div>}</div>
          ) : (
            <>
              <p className="text-sm text-muted-foreground mt-2 mb-6">Please answer honestly; "No" answers help {data.org} plan compensating controls with you. Takes about 3 minutes.</p>
              <div className="space-y-4">
                {data.questions.map((q, i) => (
                  <div key={q.id} className="border rounded-md p-3" data-testid={`vq-${q.id}`}>
                    <div className="text-sm font-medium mb-2">{i + 1}. {q.text}</div>
                    <div className="flex gap-2">{[["yes", true], ["no", false]].map(([l, v]) => <button key={l} onClick={() => setAnswers({ ...answers, [q.id]: v })} data-testid={`vq-${q.id}-${l}`} className={cn("text-sm border rounded-md px-4 py-1.5 capitalize row-hover", answers[q.id] === v && (v ? "bg-emerald-500/10 border-emerald-500 text-emerald-700 dark:text-emerald-300" : "bg-rose-500/10 border-rose-500 text-rose-700 dark:text-rose-300"))}>{l}</button>)}</div>
                  </div>
                ))}
                <Field label="Your name and role"><Input value={meta.respondent} onChange={(e) => setMeta({ ...meta, respondent: e.target.value })} data-testid="vq-respondent" /></Field>
                <Field label="Comments (optional)"><Textarea rows={2} value={meta.comments} onChange={(e) => setMeta({ ...meta, comments: e.target.value })} data-testid="vq-comments" /></Field>
                <Button className="w-full" onClick={submit} disabled={Object.keys(answers).length < data.questions.length || !meta.respondent} data-testid="vq-submit">Submit responses</Button>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
